<script lang="ts" setup>
	import { useSeoSchema } from '~/utils/seo-schema'
	const teamHeroImg = ['/v1727082913/team-office_im9gfo.webp', '/v1727082913/team-beach_rwp4rs.webp']
	const seo = {
		title: 'About MyCoverGenius | Simplifying Insurance for You',
		description:
			'Discover more about MyCoverGenius, an insurtech dedicated to making coverage simple and accessible. Discover our mission, values, and how we help protect what matters most to you.',
		tags: ['About Mycovergenius', 'Insurance Company', 'About', 'Insuretech'],
		url: `${import.meta.env.VITE_BUSINESS_URL}/about-us`
	}
	const founders = [
		{ name: 'Adebowale Adebanjo', img: '/v1727082896/Adebowale_mx71rn.webp' },
		{ name: 'Alexander Igwe', img: '/v1727082895/Alex_gjrfll.webp' },
		{ name: 'Fred Ebho', img: '/v1727082895/Fred_udludv.webp' }
	]

	const { generateAboutPageSchema } = useSeoSchema()

	useHead({
		script: [{ type: 'application/ld+json', innerHTML: JSON.stringify(generateAboutPageSchema(founders)) }],
		link: [{ rel: 'canonical', href: 'https://mycovergenius.com/about-us' }]
	})

	useSeoMeta({
		title: seo.title,
		description: seo.description,
		ogTitle: seo.title,
		ogDescription: seo.description,
		ogType: 'website',
		ogLocale: 'en_NG',
		articleTag: seo.tags,
		ogUrl: seo.url,
		twitterTitle: 'About Us | MyCoverGenius',
		twitterDescription: seo.description,
		twitterSite: '@mycovergenius',
		twitterCard: 'summary_large_image',
		twitterAppUrlGoogleplay: seo.url,
		twitterAppUrlIphone: seo.url,
		robots: {
			all: 'true',
			follow: 'true',
			index: 'true'
		}
	})
</script>

<template>
	<section>
		<header class="mt-0 px-5 md:px-0 pt-14 md:pt-[100px]">
			<ui-pad :ui="{ paddingInline: '', paddingBlock: '' }" class="w-full space-y-14 md:space-y-[166px]">
				<ui-h1
					:animate-title="['simple!', 'accessible!', 'affordable!']"
					:ui="{
						wrapperClass: {
							maxWidth: 'max-w-[1400px] mx-auto'
						},
						hClass: {
							wrapper: {
								base: '!text-wrap max-w-[1087px] mx-auto',
								size: 'text-[2rem]/[2.3rem] -tracking-[0.06rem] md:text-[3.5rem]/[3.85rem] md:-tracking-[0.1rem]  lg:text-[4.25rem]/[4.675rem] lg:-tracking-[0.1275rem]'
							}
						}
					}"
				>
					<template #title>
						<div
							class="!leading-[16px] tracking-normal md:h-[39px] flex items-center justify-center h-auto px-3 mx:px-4 lg:px-5 font-metropolis py-1.5 bg-main md:bg-white/20 rounded-full backdrop-blur-[20px] w-fit md:mx-auto mb-7 md:mb-10"
						>
							<p
								class="text-center text-white text-xs md:text-base font-medium md:font-bold leading-tight inline-flex items-center gap-[10px]"
							>
								<tools-icon dir="random" icon="people" height="16" width="16" class="md:hidden" />
								About Us
							</p>
						</div>

						<span class="block"> Every Nigerian needs insurance and we are making it </span>
					</template>
				</ui-h1>

				<div class="relative">
					<div
						class="overflow-x-auto flex items-start sm:justify-center invisible-scrollbar snap-x flex-grow gap-x-5"
					>
						<nuxt-img
							provider="cloudinary"
							v-for="(img, index) in teamHeroImg"
							:key="'hero-team-img-' + index"
							:src="img"
							loading="lazy"
							alt="team celebration"
							class="md:max-h-[380px] self-stretch object-cover snap-center max-h-[210px] flex-none"
							preload
						/>
					</div>
				</div>
			</ui-pad>
		</header>

		<div class="mt-10">
			<main-founders-story class="my-[180px]" />
			<main-team-mission class="md:my-44" />
			<main-company-values class="md:my-44" />
			<main-company-serve class="mt-[120px] md:mt-44" />

			<div class="flexed justify-center py-[120px] md:py-10 md:min-h-screen">
				<nuxt-img
					provider="cloudinary"
					src="/v1727082945/genius-logo_ynlmlr.webp"
					alt="genius"
					class="md:max-h-[525px] max-h-[170px] object-cover"
					loading="lazy"
					preload
				/>
			</div>
		</div>
	</section>
</template>

<style scoped>
	header {
		background-image: linear-gradient(to bottom, #2c554c 770px, transparent 0);
	}
</style>
